import React, { useEffect, useRef, useState } from 'react';

import { Button, Result, Spin, Typography } from 'antd';

import { useNavigate } from 'react-router-dom';

import { apiSubscriptionAPI } from '../../http/ApiSubscriptionAPI';

const { Title, Paragraph } = Typography;

type Status = 'checking' | 'success' | 'pending' | 'failed';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 10; // ~30 секунд

const PaymentSuccess: React.FC = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState<Status>('checking');
    const attemptsRef = useRef(0);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        let cancelled = false;

        const check = () => {
            apiSubscriptionAPI
                .Get()
                .then((res) => {
                    if (cancelled) {
                        return;
                    }
                    const sub = res.subscription;
                    if (res.has_subscription && sub?.status === 'active') {
                        setStatus('success');
                        return;
                    }
                    if (sub?.status === 'pending') {
                        attemptsRef.current += 1;
                        if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
                            setStatus('pending');
                            return;
                        }
                        timerRef.current = window.setTimeout(check, POLL_INTERVAL_MS);
                        return;
                    }
                    // Нет подписки или другой статус — повторяем опрос,
                    // т.к. webhook мог ещё не дойти.
                    attemptsRef.current += 1;
                    if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
                        setStatus('failed');
                        return;
                    }
                    timerRef.current = window.setTimeout(check, POLL_INTERVAL_MS);
                })
                .catch(() => {
                    if (!cancelled) {
                        setStatus('failed');
                    }
                });
        };

        check();

        return () => {
            cancelled = true;
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
            }
        };
    }, []);

    if (status === 'checking') {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <Title level={3} style={{ marginTop: '20px' }}>
                    Проверяем статус оплаты...
                </Title>
                <Paragraph>Пожалуйста, подождите</Paragraph>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <Result
                status="success"
                title="Оплата прошла успешно!"
                subTitle="Подписка на API активирована. Управлять ей можно в разделе «Настройки» → «API»."
                extra={[
                    <Button
                        type="primary"
                        key="settings"
                        onClick={() => navigate('/settings')}
                    >
                        Перейти в личный кабинет
                    </Button>,
                    <Button key="home" onClick={() => navigate('/')}>
                        На главную
                    </Button>,
                ]}
            />
        );
    }

    if (status === 'pending') {
        return (
            <Result
                status="info"
                title="Оплата обрабатывается"
                subTitle="Платёж принят, но подтверждение от ЮKassa ещё не поступило. Обычно это занимает до минуты. Обновите страницу позже или проверьте статус в разделе «Настройки» → «API»."
                extra={[
                    <Button
                        type="primary"
                        key="settings"
                        onClick={() => navigate('/settings')}
                    >
                        Перейти в личный кабинет
                    </Button>,
                    <Button key="reload" onClick={() => window.location.reload()}>
                        Обновить
                    </Button>,
                ]}
            />
        );
    }

    return (
        <Result
            status="error"
            title="Не удалось подтвердить оплату"
            subTitle="Если деньги списались, подписка активируется автоматически в течение нескольких минут. Проверьте раздел «Настройки» → «API», а при проблемах свяжитесь с поддержкой."
            extra={[
                <Button
                    type="primary"
                    key="settings"
                    onClick={() => navigate('/settings')}
                >
                    Перейти в личный кабинет
                </Button>,
                <Button key="home" onClick={() => navigate('/')}>
                    На главную
                </Button>,
            ]}
        />
    );
};

export default PaymentSuccess;
